import { watch } from "vue";

export type RouterAccessRightMetaType = {
  rights?: string[];
  accessRightSome?: boolean;
  resolvedAuthorizationSiteId?: number;
  authorizationSiteResource?:
    | "ihEquipment"
    | "ihConfig"
    | "qaChecklist"
    | "qaProductFamily"
    | "qaErrorCatalogue"
    | "bdeProductionPlanTemplate"
    | "user"
    | "tcSiteConfig"
    | "bdeDataSource"
    | "bdeProcess"
    | "bdeLabel"
    | "bdeChecklistConnection"
    | "userShiftSchedule"
    | "news"
    | "bdeView"
    | "bdeViewRead"
    | "technicalPlace";
  authorizationSiteResourceIdParam?: "id" | "productionPlanTemplateId" | "processId" | "viewId";
  authorizationSiteResourceOptional?: boolean;
  authorizationSiteResourceIsMultiSite?: boolean;
  resolvedAuthorizationSiteIds?: number[];
};

export function nonEmptyRouteParam(param: string | string[] | undefined) {
  return param === "" ? undefined : param;
}

export function authorizationResourceId(
  meta: RouterAccessRightMetaType | undefined,
  params: Record<string, string | string[] | undefined>,
): string | string[] | undefined {
  if (!meta?.authorizationSiteResource) return undefined;
  return params[meta.authorizationSiteResourceIdParam ?? "id"];
}

export function matchesAuthorizationParentId(
  actualId: number | undefined,
  routeParam: string | string[] | undefined,
): boolean {
  return (
    Number.isInteger(actualId) &&
    (actualId ?? 0) > 0 &&
    typeof routeParam === "string" &&
    /^\d+$/.test(routeParam) &&
    actualId === Number(routeParam)
  );
}

export function isSiteInAuthorizationScope(
  siteIds: readonly number[],
  candidate: string | string[] | number | undefined,
): boolean {
  const siteId =
    typeof candidate === "number"
      ? candidate
      : typeof candidate === "string" && /^\d+$/.test(candidate)
        ? Number(candidate)
        : 0;
  return Number.isInteger(siteId) && siteId > 0 && siteIds.includes(siteId);
}

export function checkRouteRights(
  meta: RouterAccessRightMetaType | undefined,
  siteIdParam: string | string[] | number | undefined,
  hasRight: (right: string, siteId?: number) => boolean,
  hasRightOnAnySite: (right: string) => boolean,
): boolean {
  const rights = meta?.rights;
  if (!rights?.length) return true;

  if (meta?.authorizationSiteResourceIsMultiSite) {
    const siteIds = meta.resolvedAuthorizationSiteIds;
    if (!siteIds?.length || siteIds.some((id) => !Number.isInteger(id) || id <= 0)) return false;
    const check = (right: string) => siteIds.every((id) => hasRight(right, id));
    return meta.accessRightSome ? rights.some(check) : rights.every(check);
  }

  const resolvedSiteId =
    meta?.authorizationSiteResource && !meta.authorizationSiteResourceOptional
      ? meta.resolvedAuthorizationSiteId
      : (meta?.resolvedAuthorizationSiteId ?? siteIdParam);
  const hasSiteParam = resolvedSiteId !== undefined;
  if (meta?.authorizationSiteResource && !hasSiteParam) return false;
  const siteId =
    typeof resolvedSiteId === "number"
      ? resolvedSiteId
      : typeof resolvedSiteId === "string" && /^\d+$/.test(resolvedSiteId)
        ? Number(resolvedSiteId)
        : 0;
  const check = (right: string) =>
    hasSiteParam ? hasRight(right, siteId) : hasRightOnAnySite(right);
  return meta?.accessRightSome ? rights.some(check) : rights.every(check);
}

type RouteRevocationState = {
  revision?: number;
  stale: boolean;
  path: string;
  ready: boolean;
  guarded: boolean;
  permitted: boolean;
};

export function watchRouteRevocation(
  currentState: () => RouteRevocationState,
  leaveRoute: () => void,
) {
  return watch(
    () => {
      const { revision, stale, path } = currentState();
      return [revision, stale, path] as const;
    },
    () => {
      const state = currentState();
      if (state.ready && state.guarded && !state.permitted) leaveRoute();
    },
  );
}
